import { useEffect, useRef, useState, useCallback } from 'react';
import type { ConcurrencySession, ConcurrencyServerMessage } from '../api/video';
import { ConcurrencyConnectionState } from '../api/video';
import { getDeviceId } from '../utils/deviceId';

// ============================================================================
// Configuration Constants
// ============================================================================

/** Interval for sending heartbeat messages to keep the connection alive */
const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds

/** Maximum number of reconnection attempts before kicking the user */
const MAX_RECONNECT_ATTEMPTS = 5;

/** Base delay for exponential backoff reconnection (2s, 4s, 8s, 16s, 32s) */
const RECONNECT_BASE_DELAY_MS = 2_000; // 2 seconds

/** Timeout for establishing initial WebSocket connection */
const CONNECTION_TIMEOUT_MS = 10_000; // 10 seconds

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface SessionStartedInfo {
  sessionId: string;
  activeSessions: number;
  limit: number;
  kickedSessions: number;
}

export interface ConcurrencySessionState {
  /** Current connection state */
  connectionState: ConcurrencyConnectionState;

  /** Whether the WebSocket is connected */
  isConnected: boolean;

  /**
   * SECURITY: Whether playback is allowed.
   *
   * This is the primary flag that should be used to control video playback.
   * When false, playback MUST be stopped to enforce concurrency limits.
   *
   * Returns false when:
   * - Concurrency session is required but WebSocket is not connected
   * - Session was kicked by server or due to connection failure
   *
   * Returns true when:
   * - No concurrency session is required (content doesn't need limit enforcement)
   * - WebSocket is connected with valid session
   * - During reconnection attempts (grace period to handle network glitches)
   */
  isPlaybackAllowed: boolean;

  /** Current session ID (null if not connected) */
  sessionId: string | null;

  /** Number of active sessions for this user */
  activeSessions: number;

  /** Maximum allowed concurrent sessions */
  limit: number;

  /** Last error that occurred */
  error: Error | null;

  /** Whether this session was kicked */
  wasKicked: boolean;

  /** Reason for being kicked (if applicable) */
  kickedReason: string | null;

  /** Message associated with being kicked */
  kickedMessage: string | null;

  /** Manually disconnect the WebSocket */
  disconnect: () => void;
}

export interface UseConcurrencySessionOptions {
  /** Called when connection is established and playback can begin */
  onSessionStarted?: (info: SessionStartedInfo) => void;

  /**
   * Called when this session is kicked - MUST stop playback immediately.
   * This can be triggered by:
   * - Server sending a 'kicked' message (another session started, admin action)
   * - WebSocket close codes 4000, 4029, 4030
   * - Max reconnection attempts exhausted
   */
  onKicked?: (reason: string, message: string) => void;

  /** Called when session count changes (for UI updates) */
  onSessionCountChanged?: (activeSessions: number, limit: number) => void;

  /** Called on connection state changes (for UI feedback) */
  onStateChanged?: (state: ConcurrencyConnectionState) => void;

  /** Called on errors */
  onError?: (error: Error) => void;

  /**
   * Optional client-provided device ID (e.g., user ID).
   * If not provided, one will be generated and persisted in localStorage.
   */
  deviceId?: string;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export const useConcurrencySession = (
  concurrencySession: ConcurrencySession | undefined,
  options: UseConcurrencySessionOptions = {}
): ConcurrencySessionState => {
  // ---- Refs for WebSocket and timers (don't trigger re-renders) ----
  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);

  /**
   * Flag to distinguish between intentional disconnects (user action, cleanup)
   * and unexpected disconnects (network issues). Only unexpected disconnects
   * should trigger reconnection attempts.
   */
  const isIntentionallyClosedRef = useRef<boolean>(false);

  /** Track the URL we're connected to, to avoid duplicate connections */
  const connectedUrlRef = useRef<string | null>(null);

  /** Guard to prevent duplicate connection attempts during the same render cycle */
  const isConnectingRef = useRef<boolean>(false);

  /** Keep options ref updated to avoid stale closures in callbacks */
  const optionsRef = useRef(options);

  // ---- State ----
  const [connectionState, setConnectionState] = useState<ConcurrencyConnectionState>(
    ConcurrencyConnectionState.DISCONNECTED
  );
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeSessions, setActiveSessions] = useState<number>(0);
  const [limit, setLimit] = useState<number>(concurrencySession?.limit ?? 0);
  const [error, setError] = useState<Error | null>(null);
  const [wasKicked, setWasKicked] = useState<boolean>(false);
  const [kickedReason, setKickedReason] = useState<string | null>(null);
  const [kickedMessage, setKickedMessage] = useState<string | null>(null);
  const [reconnectTrigger, setReconnectTrigger] = useState<number>(0);

  // Keep options ref updated
  optionsRef.current = options;

  const wsUrl = concurrencySession?.wsUrl;
  const token = concurrencySession?.token;

  // Get device ID (client-provided or auto-generated from localStorage)
  const deviceId = getDeviceId(options.deviceId);

  // ============================================================================
  // Playback Allowed Logic
  // ============================================================================
  /**
   * Determines if playback should be allowed based on kicked state.
   *
   * Playback is blocked ONLY when the user was explicitly kicked by the server
   * (concurrency limit exceeded, admin action, max reconnection attempts exhausted).
   *
   * Network issues and temporary disconnections do NOT block playback - we allow
   * the video to continue playing while attempting to reconnect.
   */
  const isPlaybackAllowed = !wasKicked;

  // ============================================================================
  // Timer Management Utilities
  // ============================================================================

  /** Stop the heartbeat interval timer */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== null) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  /** Send a heartbeat message to keep the session alive */
  const sendHeartbeat = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
    }
  }, []);

  /** Start the heartbeat interval timer */
  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatTimerRef.current = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
  }, [stopHeartbeat, sendHeartbeat]);

  /** Clear the connection timeout timer */
  const clearConnectionTimeout = useCallback(() => {
    if (connectionTimeoutRef.current !== null) {
      window.clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  /** Clear the reconnection timer */
  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  // ============================================================================
  // State Update Utilities
  // ============================================================================

  /** Update connection state and notify listener */
  const updateConnectionState = useCallback((state: ConcurrencyConnectionState) => {
    setConnectionState(state);
    optionsRef.current.onStateChanged?.(state);
  }, []);

  /**
   * Kick the user due to connection failure.
   * This is called when:
   * - Max reconnection attempts are exhausted
   * - Server sends specific close codes (4000, 4029, 4030)
   *
   * Note: We only set the reason code here, not a hardcoded message.
   * The UI layer should use the reason to look up the appropriate localized message.
   */
  const kickDueToConnectionFailure = useCallback((reason: string) => {
    setWasKicked(true);
    setKickedReason(reason);
    setKickedMessage(null);
    optionsRef.current.onKicked?.(reason, '');
  }, []);

  // ============================================================================
  // Cleanup and Disconnect
  // ============================================================================

  /** Clean up all timers and close WebSocket connection */
  const cleanup = useCallback(() => {
    stopHeartbeat();
    clearConnectionTimeout();
    clearReconnectTimer();

    if (wsRef.current) {
      // Close with normal closure code if still open/connecting
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close(1000, 'Client disconnecting');
      }
      wsRef.current = null;
    }

    connectedUrlRef.current = null;
    setSessionId(null);
    updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
  }, [stopHeartbeat, clearConnectionTimeout, clearReconnectTimer, updateConnectionState]);

  /** Public method to manually disconnect (marks as intentional to prevent reconnection) */
  const disconnect = useCallback(() => {
    isIntentionallyClosedRef.current = true;
    cleanup();
  }, [cleanup]);

  // ============================================================================
  // WebSocket Message Handler
  // ============================================================================

  /** Handle incoming messages from the concurrency server */
  const handleMessage = useCallback((data: string) => {
    let message: ConcurrencyServerMessage;
    try {
      message = JSON.parse(data);
    } catch {
      console.error('[ConcurrencySession] Failed to parse message:', data);
      const parseError = new Error('Invalid JSON from server');
      setError(parseError);
      optionsRef.current.onError?.(parseError);
      return;
    }

    switch (message.type) {
      case 'session_started':
        // Session successfully established - playback can begin
        setSessionId(message.sessionId);
        setActiveSessions(message.activeSessions);
        setLimit(message.limit);
        setError(null);
        // Clear any previous kicked state
        setWasKicked(false);
        setKickedReason(null);
        setKickedMessage(null);
        updateConnectionState(ConcurrencyConnectionState.CONNECTED);
        startHeartbeat();

        optionsRef.current.onSessionStarted?.({
          sessionId: message.sessionId,
          activeSessions: message.activeSessions,
          limit: message.limit,
          kickedSessions: message.kickedSessions,
        });
        break;

      case 'heartbeat_ack':
        // Heartbeat acknowledged - connection is healthy
        setActiveSessions(message.activeSessions);
        break;

      case 'session_count_changed':
        // Another session connected or disconnected (for UI updates)
        setActiveSessions(message.activeSessions);
        setLimit(message.limit);
        optionsRef.current.onSessionCountChanged?.(message.activeSessions, message.limit);
        break;

      case 'kicked':
        // CRITICAL: This session was kicked - MUST stop playback immediately
        // Mark as intentional to prevent reconnection attempts
        isIntentionallyClosedRef.current = true;
        setWasKicked(true);
        setKickedReason(message.reason);
        setKickedMessage(message.message);
        optionsRef.current.onKicked?.(message.reason, message.message);
        cleanup();
        break;

      case 'error':
        // Server reported an error
        const serverError = new Error(message.message);
        setError(serverError);
        optionsRef.current.onError?.(serverError);
        break;
    }
  }, [updateConnectionState, startHeartbeat, cleanup]);

  // ============================================================================
  // Reconnection Logic
  // ============================================================================

  /**
   * Schedule a reconnection attempt with exponential backoff.
   *
   * SECURITY: If max attempts are exhausted, the user is kicked to prevent
   * bypassing concurrency limits by staying in a perpetual "reconnecting" state.
   */
  const scheduleReconnect = useCallback(() => {
    clearReconnectTimer();

    // Check if we've exhausted all reconnection attempts
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      // SECURITY: Kick the user - can't maintain valid session
      kickDueToConnectionFailure('connection_failed');
      updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
      const maxRetriesError = new Error('Max reconnection attempts reached');
      setError(maxRetriesError);
      optionsRef.current.onError?.(maxRetriesError);
      return;
    }

    // Calculate delay with exponential backoff: 2s, 4s, 8s, 16s, 32s
    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current);
    reconnectAttemptsRef.current++;

    updateConnectionState(ConcurrencyConnectionState.RECONNECTING);

    reconnectTimerRef.current = window.setTimeout(() => {
      // Trigger reconnect by clearing the connected URL and incrementing the trigger
      connectedUrlRef.current = null;
      isConnectingRef.current = false;
      // Force re-run of the connection effect
      setReconnectTrigger((prev) => prev + 1);
    }, delay);
  }, [clearReconnectTimer, kickDueToConnectionFailure, updateConnectionState]);

  // ============================================================================
  // WebSocket Close Handler
  // ============================================================================

  /**
   * Handle WebSocket close events.
   *
   * Different close codes have different meanings:
   * - 1000: Normal closure (intentional)
   * - 4000: Replaced by same device
   * - 4029: Concurrency limit exceeded
   * - 4030: Kicked by admin
   * - Other: Network error - attempt reconnection
   */
  const handleClose = useCallback((code: number) => {
    stopHeartbeat();
    clearConnectionTimeout();

    // Handle specific close codes that indicate definitive rejection (no reconnection)
    switch (code) {
      case 4029:
        // Concurrency limit exceeded on connect - kick user
        updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
        kickDueToConnectionFailure('concurrency_limit_exceeded');
        return;

      case 4030:
        // Kicked by admin - kick user
        updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
        kickDueToConnectionFailure('admin_action');
        return;

      case 4000:
        // Replaced by same device (shouldn't happen normally)
        updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
        kickDueToConnectionFailure('session_replaced');
        return;

      case 1000:
        // Normal closure (intentional disconnect)
        updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
        return;
    }

    // For other close codes (network issues, server restart, etc.),
    // attempt reconnection ONLY if this wasn't an intentional close
    if (!isIntentionallyClosedRef.current && wsUrl && token) {
      scheduleReconnect();
    } else {
      updateConnectionState(ConcurrencyConnectionState.DISCONNECTED);
    }
  }, [stopHeartbeat, clearConnectionTimeout, updateConnectionState, kickDueToConnectionFailure, scheduleReconnect, wsUrl, token]);

  // ============================================================================
  // WebSocket Connection Effect
  // ============================================================================

  useEffect(() => {
    // If no concurrency session, clean up any existing connection
    if (!wsUrl || !token) {
      if (wsRef.current) {
        isIntentionallyClosedRef.current = true;
        cleanup();
      }
      isConnectingRef.current = false;
      return;
    }

    // CRITICAL: If user was kicked, do NOT attempt to reconnect
    // This prevents the flickering error screen issue
    if (wasKicked) {
      return;
    }

    // Build the full URL with token as query parameter
    const fullUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;

    // Skip if already connected to the same URL
    if (connectedUrlRef.current === fullUrl && wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // Skip if WebSocket is already in the process of connecting
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Guard against duplicate connection attempts in the same render cycle
    // This prevents double connections caused by React StrictMode or rapid re-renders
    if (isConnectingRef.current) {
      return;
    }
    isConnectingRef.current = true;

    // Close existing connection if connecting to a different URL
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Only reset error state if we weren't kicked
    // This ensures we don't try to reconnect after being kicked
    if (!isIntentionallyClosedRef.current) {
      setError(null);
    }
    updateConnectionState(ConcurrencyConnectionState.CONNECTING);

    try {
      const ws = new WebSocket(fullUrl);

      // Set connection timeout - if connection doesn't establish in time, try reconnecting
      connectionTimeoutRef.current = window.setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          isConnectingRef.current = false;
          ws.close();
          // Connection timeout - try to reconnect
          if (!isIntentionallyClosedRef.current) {
            scheduleReconnect();
          }
        }
      }, CONNECTION_TIMEOUT_MS);

      ws.onopen = () => {
        clearConnectionTimeout();
        connectedUrlRef.current = fullUrl;
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        // Note: Don't update state to CONNECTED here
        // Wait for 'session_started' message from server to confirm session is valid
      };

      ws.onmessage = (event) => {
        handleMessage(event.data);
      };

      ws.onerror = (event) => {
        console.error('[ConcurrencySession] WebSocket error:', event);
        const wsError = new Error('WebSocket connection error');
        setError(wsError);
        optionsRef.current.onError?.(wsError);
      };

      ws.onclose = (event) => {
        clearConnectionTimeout();
        connectedUrlRef.current = null;
        isConnectingRef.current = false;
        handleClose(event.code);
      };

      wsRef.current = ws;
    } catch (err) {
      isConnectingRef.current = false;
      const connectionError = err instanceof Error ? err : new Error('Failed to connect to WebSocket');
      setError(connectionError);
      optionsRef.current.onError?.(connectionError);
      // Connection failed - try to reconnect
      if (!isIntentionallyClosedRef.current) {
        scheduleReconnect();
      }
    }
  }, [wsUrl, token, deviceId, wasKicked, reconnectTrigger, cleanup, handleMessage, handleClose, clearConnectionTimeout, updateConnectionState, scheduleReconnect]);

  // ============================================================================
  // Browser Event Handlers
  // ============================================================================

  /**
   * Handle page unload (tab close, navigation away).
   * Gracefully disconnect to free up the session slot.
   */
  useEffect(() => {
    if (!concurrencySession) return;

    const handleBeforeUnload = () => {
      disconnect();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [disconnect, concurrencySession]);

  /**
   * Cleanup on component unmount.
   * Mark as intentional close to prevent reconnection attempts.
   */
  useEffect(() => {
    return () => {
      isIntentionallyClosedRef.current = true;
      cleanup();
    };
  }, [cleanup]);

  // ============================================================================
  // Return State
  // ============================================================================

  return {
    connectionState,
    isConnected: connectionState === ConcurrencyConnectionState.CONNECTED,
    isPlaybackAllowed,
    sessionId,
    activeSessions,
    limit,
    error,
    wasKicked,
    kickedReason,
    kickedMessage,
    disconnect,
  };
};
